import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { CheckCircle2, Mail, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Textarea } from "@/components/ui/Textarea";
import { SectionContainer } from "@/components/marketing/SectionContainer";
import { apiFetch, ApiError } from "@/lib/api";
import { usePageMeta } from "./usePageMeta";

const ContactSchema = z.object({
  name: z.string().min(2, "Please enter your name"),
  email: z.string().email("Please enter a valid email"),
  company: z.string().min(2, "Please enter your company"),
  message: z.string().min(10, "Please share a few sentences"),
});

type ContactValues = z.infer<typeof ContactSchema>;

export function Contact() {
  usePageMeta({
    title: "Contact",
    description:
      "Pilot interest, enterprise sales, press — reach us at hello@virtualdesk.ai or send us a message. We reply within one business day.",
  });
  const [sent, setSent] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ContactValues>({
    resolver: zodResolver(ContactSchema),
  });

  const onSubmit = async (values: ContactValues) => {
    try {
      await apiFetch<{ ok: true }>("/api/v1/public/contact", {
        method: "POST",
        body: JSON.stringify(values),
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 501) {
        // Expected — backend endpoint not shipped yet. Be optimistic on the UI.
      } else {
        // eslint-disable-next-line no-console
        console.error("contact submit failed", err);
      }
    } finally {
      setSent(true);
      reset();
    }
  };

  return (
    <SectionContainer>
      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-20 items-start">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wider text-brand-violet">
            Contact
          </p>
          <h1 className="mt-3 font-display text-5xl md:text-6xl lg:text-7xl tracking-wide leading-[0.95] text-brand-ink">
            Let's talk.
          </h1>
          <p className="mt-6 text-lg text-brand-ink/70 leading-relaxed">
            Pilot interest, enterprise sales, press inquiries, or just saying hi — we read every
            message and reply within one business day.
          </p>

          <div className="mt-8 space-y-5">
            <div className="flex items-start gap-4">
              <div className="rounded-lg bg-brand-violet/15 p-2.5 text-brand-violet">
                <Mail className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-brand-ink">Email</p>
                <p className="mt-1 text-sm text-brand-ink/70">hello@virtualdesk.ai</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="rounded-lg bg-brand-violet/15 p-2.5 text-brand-violet">
                <MessageSquare className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-brand-ink">Enterprise sales</p>
                <p className="mt-1 text-sm text-brand-ink/70">
                  sales@virtualdesk.ai — custom pricing, SSO, DPA
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/5 p-6 md:p-8">
          {sent ? (
            <div className="flex flex-col items-center text-center py-10">
              <CheckCircle2 className="h-12 w-12 text-brand-violet" />
              <h2 className="mt-5 font-display text-3xl tracking-wide text-brand-ink">
                Thanks. We'll be in touch.
              </h2>
              <p className="mt-3 text-sm text-brand-ink/70 max-w-sm">
                We reply within one business day. For urgent pilot requests, follow up directly at
                hello@virtualdesk.ai.
              </p>
            </div>
          ) : (
            <form className="space-y-5" onSubmit={handleSubmit(onSubmit)} noValidate>
              <div>
                <Label htmlFor="name">Your name</Label>
                <Input
                  id="name"
                  autoComplete="name"
                  className="mt-2 bg-brand-midnight/60"
                  {...register("name")}
                  aria-invalid={!!errors.name}
                />
                {errors.name && (
                  <p className="mt-1.5 text-xs text-signal-failed">{errors.name.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="email">Work email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  className="mt-2 bg-brand-midnight/60"
                  {...register("email")}
                  aria-invalid={!!errors.email}
                />
                {errors.email && (
                  <p className="mt-1.5 text-xs text-signal-failed">{errors.email.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="company">Company</Label>
                <Input
                  id="company"
                  autoComplete="organization"
                  className="mt-2 bg-brand-midnight/60"
                  {...register("company")}
                  aria-invalid={!!errors.company}
                />
                {errors.company && (
                  <p className="mt-1.5 text-xs text-signal-failed">{errors.company.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="message">How can we help?</Label>
                <Textarea
                  id="message"
                  rows={5}
                  className="mt-2 bg-brand-midnight/60"
                  {...register("message")}
                  aria-invalid={!!errors.message}
                />
                {errors.message && (
                  <p className="mt-1.5 text-xs text-signal-failed">{errors.message.message}</p>
                )}
              </div>

              <Button type="submit" variant="primary" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? "Sending..." : "Send message"}
              </Button>
              <p className="text-xs text-brand-ink/50 text-center">
                By submitting you agree to our Terms and Privacy Policy.
              </p>
            </form>
          )}
        </div>
      </div>
    </SectionContainer>
  );
}
